<template>
    <div class="book-card">
        <a :href="`/book/${book.id}`" class="book-image-link">
            <div :style="getImageContainerStyle()" class="book-image-container">
                <div v-if="imageLoading && book.coverImage" class="book-image-loader">
                    <Loader size="small" />
                </div>
                <img v-if="book.coverImage" ref="imgRef" :src="book.coverImage" :alt="book.title" class="book-image"
                    @load="imageLoading = false" @error="imageLoading = false"
                    :class="{ 'book-image-loading': imageLoading }" />
                <div v-if="!book.coverImage" class="book-image-placeholder">
                    <svg class="book-placeholder-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                </div>
            </div>
        </a>
        <div class="book-text-content" :style="getTextContentStyle()">
            <a :href="`/book/${book.id}`" class="book-title-link">
                <h3 class="book-title" data-no-translate>{{ book.title }}</h3>
            </a>
            <a :href="`/author/${encodeURIComponent(book.author)}`" class="book-author-link">
                <p class="book-author" data-no-translate>{{ book.author }}</p>
            </a>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import Loader from './Loader.vue'
import { useBookImageStyle } from '../composables/useBookImageStyle'

const { getBookImageContainerStyle } = useBookImageStyle()

const props = defineProps({
    book: {
        type: Object,
        required: true
    }
})

const imageLoading = ref(true)

const handleImageLoad = () => {
    imageLoading.value = false
}

const handleImageError = () => {
    imageLoading.value = false
}

watch(() => props.book.coverImage, (newImage) => {
    if (newImage) {
        imageLoading.value = true
        const img = new Image()
        img.onload = handleImageLoad
        img.onerror = handleImageError
        img.src = newImage
    } else {
        imageLoading.value = false
    }
})

onMounted(() => {
    if (props.book.coverImage) {
        const img = new Image()
        img.onload = handleImageLoad
        img.onerror = handleImageError
        img.src = props.book.coverImage
    } else {
        imageLoading.value = false
    }
})

const getImageContainerStyle = () => {
    return getBookImageContainerStyle(props.book)
}

const getTextContentStyle = () => {
    const imageStyle = getBookImageContainerStyle(props.book)
    // Match the width of the text content to the image container
    return {
        width: imageStyle.width,
        maxWidth: imageStyle.width
    }
}
</script>

<style scoped>
.book-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 1rem;
    flex-shrink: 0;
}

.book-image-link {
    display: block;
    margin-bottom: 0.5rem;
}

.book-image-container {
    background-color: #e5e7eb;
    overflow: hidden;
    transition: box-shadow 0.2s;
    cursor: pointer;
    position: relative;
    border-radius: 0;
}

.book-image-container:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.book-image-loader {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.book-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.2s;
}

.book-image-loading {
    opacity: 0;
}

.book-image-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
}

.book-placeholder-icon {
    width: 4rem;
    height: 4rem;
}

.book-text-content {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
}

.book-title-link {
    display: block;
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    text-decoration: none;
}

.book-author-link {
    display: block;
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    text-decoration: none;
    margin-top: 0;
}

.book-title {
    font-family: 'EB Garamond', serif;
    font-weight: 600;
    color: #111111;
    font-size: 1.059rem;
    line-height: 1.25rem;
    margin: 0;
    padding: 0;
    text-decoration: underline;
    word-wrap: break-word;
    word-break: break-word;
    overflow-wrap: break-word;
}

.book-author {
    white-space: normal;
    word-wrap: break-word;
    overflow-wrap: break-word;
    margin: 0;
    padding: 0;
    padding-bottom: 0.25rem;
    line-height: 1.4;
    font-size: 0.908rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #111111;
    text-decoration: underline;
}
</style>
